import type { Prisma, PrismaClient } from '@prisma/client';
import { audit, type AuditActor } from '../../core/audit.js';
import { HttpError } from '../../core/errors.js';
import { MAX_CSV_BYTES, MAX_CSV_ROWS, parseCsv, toCsv, unneutraliseCell } from './csv.parser.js';
import {
  DEFAULT_OPTIONS,
  getImportEntity,
  type ImportContext,
  type ImportEntity,
  type ImportOptions,
} from './csv.registry.js';

type Mapping = Record<string, string>; // entity column key -> CSV header

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Auto-match CSV headers to entity columns by key, label or alias. */
export function suggestMapping(entity: ImportEntity, headers: string[]): Mapping {
  const mapping: Mapping = {};
  for (const col of entity.columns) {
    const names = [col.key, col.label, ...(col.aliases ?? [])].map(norm);
    const hit = headers.find((h) => names.includes(norm(h)));
    if (hit) mapping[col.key] = hit;
  }
  return mapping;
}

export function templateCsv(entity: ImportEntity): string {
  return toCsv(
    entity.columns.map((c) => c.key),
    [entity.columns.map((c) => c.example)],
  );
}

function entityOr404(key: string): ImportEntity {
  const e = getImportEntity(key);
  if (!e) throw new HttpError(404, 'NOT_FOUND', `Unknown import type "${key}"`);
  return e;
}

/** Step 2: store the uploaded file. Nothing is validated or saved to business tables yet. */
export async function createBatch(
  db: PrismaClient,
  args: { companyId: number; userId: number; entity: string; filename: string; text: string },
) {
  const entity = entityOr404(args.entity);
  if (Buffer.byteLength(args.text) > MAX_CSV_BYTES) {
    throw new HttpError(
      413,
      'FILE_TOO_LARGE',
      `The file is larger than ${MAX_CSV_BYTES / 1024 / 1024} MB`,
    );
  }
  const rows = parseCsv(args.text);
  if (rows.length < 2)
    throw new HttpError(400, 'EMPTY_FILE', 'The file needs a header row and at least one data row');
  if (rows.length - 1 > MAX_CSV_ROWS) {
    throw new HttpError(413, 'TOO_MANY_ROWS', `At most ${MAX_CSV_ROWS} rows per import`);
  }
  const headers = rows[0]!.map((h) => h.trim());
  const batch = await db.importBatch.create({
    data: {
      companyId: args.companyId,
      entity: entity.key,
      filename: args.filename.slice(0, 255),
      totalRows: rows.length - 1,
      rawCsv: args.text,
      createdBy: args.userId,
    },
  });
  return {
    id: batch.id,
    headers,
    totalRows: batch.totalRows,
    sample: rows.slice(1, 4),
    mapping: suggestMapping(entity, headers),
    columns: entity.columns,
  };
}

async function loadBatch(db: PrismaClient, companyId: number, id: number) {
  const b = await db.importBatch.findFirst({ where: { id, companyId } });
  if (!b) throw new HttpError(404, 'NOT_FOUND', 'Import not found');
  return b;
}

function rowsOf(rawCsv: string, mapping: Mapping) {
  const table = parseCsv(rawCsv);
  const headers = table[0]!.map((h) => h.trim());
  return table.slice(1).map((cells, i) => {
    const raw: Record<string, string> = {};
    for (const [key, header] of Object.entries(mapping)) {
      const idx = headers.indexOf(header);
      raw[key] = idx >= 0 ? unneutraliseCell((cells[idx] ?? '').trim()) : '';
    }
    return { rowNumber: i + 2, raw };
  });
}

export interface DryRunSummary {
  batchId: number;
  total: number;
  valid: number;
  warnings: number;
  errors: number;
  missingRefs: { kind: string; value: string; count: number }[];
  canCommit: boolean;
  firstErrors: { rowNumber: number; errors: string[] }[];
}

/**
 * Step 4: validate EVERY row with the same schemas the UI uses. Nothing is saved to business
 * tables; per-row results are stored so an error report can be downloaded.
 */
export async function dryRun(
  db: PrismaClient,
  args: {
    companyId: number;
    userId: number;
    batchId: number;
    mapping: Mapping;
    options?: Partial<ImportOptions>;
  },
): Promise<DryRunSummary> {
  const batch = await loadBatch(db, args.companyId, args.batchId);
  if (batch.status === 'committed' || batch.status === 'rolled_back') {
    throw new HttpError(409, 'ALREADY_DONE', 'This import has already been committed');
  }
  const entity = entityOr404(batch.entity);
  const options: ImportOptions = {
    duplicate: args.options?.duplicate ?? DEFAULT_OPTIONS.duplicate,
    missingRef: args.options?.missingRef ?? DEFAULT_OPTIONS.missingRef,
    asApproved: args.options?.asApproved ?? false,
  };
  const missingRequired = entity.columns
    .filter((c) => c.required && !args.mapping[c.key])
    .map((c) => c.label);
  if (missingRequired.length) {
    throw new HttpError(
      400,
      'MAPPING_INCOMPLETE',
      `Map a column for: ${missingRequired.join(', ')}`,
    );
  }

  const rows = rowsOf(batch.rawCsv ?? '', args.mapping);
  const results: Prisma.ImportRowCreateManyInput[] = [];
  const missingCounts = new Map<string, { kind: string; value: string; count: number }>();
  const seenKeys = new Map<string, number>();

  await db.$transaction(async (tx) => {
    const ctx: ImportContext = {
      tx,
      companyId: args.companyId,
      userId: args.userId,
      options,
      cache: new Map(),
    };
    // Grouped entities (documents with lines) turn several CSV rows into one record.
    const units: { rowNumber: number; rows: typeof rows }[] = [];
    if (entity.groupKey && entity.parseGroup) {
      const byKey = new Map<string, (typeof rows)[number][]>();
      for (const r of rows) {
        const key = (r.raw[entity.groupKey] ?? '').trim();
        if (!key) units.push({ rowNumber: r.rowNumber, rows: [r] });
        else {
          const list = byKey.get(key.toLowerCase()) ?? [];
          list.push(r);
          byKey.set(key.toLowerCase(), list);
        }
      }
      for (const list of byKey.values()) units.push({ rowNumber: list[0]!.rowNumber, rows: list });
      units.sort((a, b) => a.rowNumber - b.rowNumber);
    } else {
      for (const r of rows) units.push({ rowNumber: r.rowNumber, rows: [r] });
    }
    for (const unit of units) {
      const { rowNumber } = unit;
      const raw = unit.rows[0]!.raw;
      const parsed = entity.parseGroup ? entity.parseGroup(unit.rows) : entity.parseRow(raw);
      if (!parsed.ok) {
        results.push({
          batchId: batch.id,
          rowNumber,
          status: 'error',
          data: raw as Prisma.InputJsonValue,
          errors: parsed.errors,
        });
        continue;
      }
      const value = parsed.value;
      const errors: string[] = [];
      const warnings: string[] = [];

      const key = entity.rowKey(value);
      if (key) {
        const first = seenKeys.get(key);
        if (first) errors.push(`Duplicate of row ${first} in this file`);
        else seenKeys.set(key, rowNumber);
      }

      errors.push(...((await entity.validate?.(ctx, value)) ?? []));

      const existing = await entity.findExisting(ctx, value);
      if (existing) {
        if (options.duplicate === 'fail') errors.push(`Already exists (#${existing.id})`);
        else if (options.duplicate === 'skip')
          warnings.push(`Already exists (#${existing.id}): will be skipped`);
        else warnings.push(`Already exists (#${existing.id}): will be updated`);
      }

      const refs = (await entity.missingRefs?.(ctx, value)) ?? [];
      for (const ref of refs) {
        const k = `${ref.kind}:${ref.value}`;
        const entry = missingCounts.get(k) ?? { kind: ref.kind, value: ref.value, count: 0 };
        entry.count += 1;
        missingCounts.set(k, entry);
        if (options.missingRef.mode === 'create')
          warnings.push(`${ref.kind} "${ref.value}" does not exist: will be created as Incomplete`);
        else if (options.missingRef.mode === 'map') {
          if (options.missingRef.map?.[ref.value])
            warnings.push(
              `${ref.kind} "${ref.value}" mapped to #${options.missingRef.map[ref.value]}`,
            );
          else errors.push(`${ref.kind} "${ref.value}" does not exist and has not been mapped`);
        } else errors.push(`${ref.kind} "${ref.value}" does not exist`);
      }

      results.push({
        batchId: batch.id,
        rowNumber,
        status: errors.length ? 'error' : warnings.length ? 'warning' : 'valid',
        data: value as Prisma.InputJsonValue,
        errors: errors.length ? errors : warnings.length ? warnings : undefined,
        action: existing ? (options.duplicate === 'update' ? 'update' : 'skip') : 'create',
        entityId: existing?.id,
      });
    }
  });

  const counts = { valid: 0, warning: 0, error: 0 };
  for (const r of results) counts[r.status as keyof typeof counts] += 1;

  await db.$transaction([
    db.importRow.deleteMany({ where: { batchId: batch.id } }),
    db.importRow.createMany({ data: results }),
    db.importBatch.update({
      where: { id: batch.id },
      data: {
        status: 'validated',
        mapping: args.mapping,
        options: options as unknown as Prisma.InputJsonValue,
        validRows: counts.valid,
        warningRows: counts.warning,
        errorRows: counts.error,
      },
    }),
  ]);

  return {
    batchId: batch.id,
    total: results.length,
    valid: counts.valid,
    warnings: counts.warning,
    errors: counts.error,
    missingRefs: [...missingCounts.values()],
    canCommit: counts.error === 0 && results.length > 0,
    firstErrors: results
      .filter((r) => r.status === 'error')
      .slice(0, 50)
      .map((r) => ({ rowNumber: r.rowNumber, errors: (r.errors as string[]) ?? [] })),
  };
}

/** Downloadable error report: row numbers and reasons. */
export async function errorReportCsv(
  db: PrismaClient,
  companyId: number,
  batchId: number,
): Promise<string> {
  const batch = await loadBatch(db, companyId, batchId);
  const rows = await db.importRow.findMany({
    where: { batchId: batch.id, status: { in: ['error', 'warning'] } },
    orderBy: { rowNumber: 'asc' },
  });
  return toCsv(
    ['row', 'severity', 'reason'],
    rows.flatMap((r) =>
      ((r.errors as string[] | null) ?? []).map((m) => [r.rowNumber, r.status, m]),
    ),
  );
}

/**
 * Step 6: apply everything in ONE database transaction. Refuses to run while any row has an error,
 * so a bad file saves nothing.
 */
export async function commitBatch(
  db: PrismaClient,
  actor: AuditActor & { userId: number },
  batchId: number,
): Promise<{ created: number; updated: number; skipped: number }> {
  const batch = await loadBatch(db, actor.companyId, batchId);
  if (batch.status !== 'validated')
    throw new HttpError(409, 'NOT_VALIDATED', 'Run the check (dry run) before importing');
  if (batch.errorRows > 0)
    throw new HttpError(
      409,
      'HAS_ERRORS',
      `${batch.errorRows} row(s) have errors. Fix the file and check again. Nothing was saved.`,
    );
  const entity = entityOr404(batch.entity);
  const options = (batch.options as unknown as ImportOptions) ?? DEFAULT_OPTIONS;
  const rows = await db.importRow.findMany({ where: { batchId }, orderBy: { rowNumber: 'asc' } });

  const totals = { created: 0, updated: 0, skipped: 0 };
  await db.$transaction(
    async (tx) => {
      const ctx: ImportContext = {
        tx,
        companyId: actor.companyId,
        userId: actor.userId,
        options,
        cache: new Map(),
      };
      const refs = new Map<string, number>();
      const createdRefs: { kind: string; id: number }[] = [];
      for (const row of rows) {
        const value = row.data as never;
        for (const ref of (await entity.missingRefs?.(ctx, value)) ?? []) {
          const k = `${ref.kind}:${ref.value}`;
          if (refs.has(k)) continue;
          if (options.missingRef.mode === 'map' && options.missingRef.map?.[ref.value])
            refs.set(k, options.missingRef.map[ref.value]!);
          else if (options.missingRef.mode === 'create' && entity.createRef) {
            const id = await entity.createRef(ctx, ref);
            refs.set(k, id);
            createdRefs.push({ kind: ref.kind, id });
          }
        }
        if (row.action === 'skip') {
          await tx.importRow.update({ where: { id: row.id }, data: { status: 'skipped' } });
          totals.skipped += 1;
        } else if (row.action === 'update' && row.entityId) {
          const existing = await entity.findExisting(ctx, value);
          await entity.update(ctx, row.entityId, value, refs);
          await tx.importRow.update({
            where: { id: row.id },
            data: {
              status: 'updated',
              before: (existing?.snapshot ?? undefined) as Prisma.InputJsonValue | undefined,
            },
          });
          totals.updated += 1;
        } else {
          const created = await entity.create(ctx, value, refs);
          await tx.importRow.update({
            where: { id: row.id },
            data: { status: 'created', entityId: created.id },
          });
          totals.created += 1;
        }
      }
      await tx.importBatch.update({
        where: { id: batchId },
        data: {
          status: 'committed',
          committedAt: new Date(),
          options: { ...options, createdRefs } as unknown as Prisma.InputJsonValue,
        },
      });
      await audit(tx, actor, {
        action: 'import.committed',
        entity: 'import_batch',
        entityId: batchId,
        after: { entity: batch.entity, file: batch.filename, ...totals },
      });
    },
    { timeout: 120_000, maxWait: 20_000 },
  );
  return totals;
}

/** Undo a committed batch: delete what it created, restore what it updated. */
export async function rollbackBatch(
  db: PrismaClient,
  actor: AuditActor & { userId: number },
  batchId: number,
) {
  const batch = await loadBatch(db, actor.companyId, batchId);
  if (batch.status !== 'committed')
    throw new HttpError(409, 'NOT_COMMITTED', 'Only a committed import can be rolled back');
  const entity = entityOr404(batch.entity);
  const rows = await db.importRow.findMany({ where: { batchId }, orderBy: { rowNumber: 'desc' } });

  await db.$transaction(
    async (tx) => {
      const ctx: ImportContext = {
        tx,
        companyId: actor.companyId,
        userId: actor.userId,
        options: DEFAULT_OPTIONS,
        cache: new Map(),
      };
      // First pass: make sure nothing has been used since the import; refuse the whole rollback if so.
      for (const row of rows) {
        if (row.status === 'created' && row.entityId) {
          const problem = await entity.remove(ctx, row.entityId);
          if (problem)
            throw new HttpError(409, 'CANNOT_ROLL_BACK', `Row ${row.rowNumber}: ${problem}`);
        } else if (row.status === 'updated' && row.entityId && row.before) {
          await entity.restore(ctx, row.entityId, row.before);
        }
      }
      const created =
        (batch.options as { createdRefs?: { kind: string; id: number }[] } | null)?.createdRefs ??
        [];
      for (const ref of created) {
        const problem = await entity.removeRef?.(ctx, ref);
        if (problem) throw new HttpError(409, 'CANNOT_ROLL_BACK', problem);
      }
      await tx.importBatch.update({
        where: { id: batchId },
        data: { status: 'rolled_back', rolledBackAt: new Date() },
      });
      await audit(tx, actor, {
        action: 'import.rolled_back',
        entity: 'import_batch',
        entityId: batchId,
        after: { entity: batch.entity },
      });
    },
    { timeout: 120_000, maxWait: 20_000 },
  );
}

export function batchView(b: {
  id: number;
  entity: string;
  filename: string;
  status: string;
  totalRows: number;
  validRows: number;
  warningRows: number;
  errorRows: number;
  createdAt: Date;
  committedAt: Date | null;
  rolledBackAt: Date | null;
}) {
  return {
    id: b.id,
    entity: b.entity,
    filename: b.filename,
    status: b.status,
    totalRows: b.totalRows,
    validRows: b.validRows,
    warningRows: b.warningRows,
    errorRows: b.errorRows,
    createdAt: b.createdAt,
    committedAt: b.committedAt,
    rolledBackAt: b.rolledBackAt,
  };
}
